import { useFormContext } from "react-hook-form";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";
import type { BidPostData } from "validations";

import { ControlledIntegerInput } from "@krea/shared/design-system/ControlledInputs/IntegerInput";
import { MoneyInput } from "@krea/shared/design-system/ControlledInputs/MoneyInput";
import type { Currency } from "@krea/shared/types/common";

import type { IPurchaseFinancingBidInput } from "@/features/lender-responses/types/lender-response";

import DetailsItem from "../../../../details/item";

interface IFormProps {
  bidFormIndex: number;
  currency: Currency;
  isEditing: boolean;
  isInterestRatePerYearActivated?: boolean;
}

export const PurchaseFinancingForm = ({
  bidFormIndex,
  currency,
  isEditing,
  isInterestRatePerYearActivated,
}: IFormProps) => {
  const [, calculatedCurrency] = useFormatCurrency({
    currency,
  });

  const { control } = useFormContext<BidPostData<IPurchaseFinancingBidInput>>();

  return (
    <div className="flex w-full flex-col">
      <div className="w-full">
        <DetailsItem
          title={
            isEditing
              ? "Update purchase order financing terms"
              : "Purchase financing terms"
          }
          className="mb-0"
        >
          <div className="flex w-full flex-col gap-4">
            <MoneyInput
              name={`bids.${bidFormIndex}.amount`}
              label="Amount"
              control={control}
              required
              currency={calculatedCurrency!}
            />
          </div>
        </DetailsItem>

        <DetailsItem title="">
          <div className="flex w-full flex-col gap-4">
            <div className="mb-4 mt-2">
              <span
                className="italic text-gray-600"
                style={{ fontSize: "14px" }}
              >
                Here you can explain other costs & qualitative information about
                your offer
              </span>
            </div>

            <div className="flex w-full flex-col gap-4">
              <ControlledIntegerInput
                name={`bids.${bidFormIndex}.lenderMonthlyInterest`}
                label={
                  isInterestRatePerYearActivated
                    ? "Interest (per year)"
                    : "Interest (per month)"
                }
                control={control}
                required
                rightText="%"
                placeholder='Value in "%"'
              />

              <ControlledIntegerInput
                name={`bids.${bidFormIndex}.paybackTimeDays`}
                label="Payback time (days)"
                control={control}
                required
                placeholder="Number of days"
              />

              <ControlledIntegerInput
                name={`bids.${bidFormIndex}.limitFeePerYear`}
                label="Limit fee (per year)"
                control={control}
                rightText="%"
                placeholder='Value in "%"'
              />

              <MoneyInput
                name={`bids.${bidFormIndex}.setupFee`}
                label="Setup fee"
                control={control}
                currency={calculatedCurrency!}
              />

              <MoneyInput
                name={`bids.${bidFormIndex}.postalFee`}
                label="Postal fee (per month)"
                control={control}
                currency={calculatedCurrency!}
              />

              <MoneyInput
                name={`bids.${bidFormIndex}.directDebitFee`}
                label="Direct debit fee (per month)"
                control={control}
                currency={calculatedCurrency!}
              />
            </div>
          </div>
        </DetailsItem>
      </div>
    </div>
  );
};
